import { motion } from "framer-motion";
import {
  Search,
  MessageSquareOff,
  Database,
  BrainCircuit,
  ShieldCheck,
  Gauge,
} from "lucide-react";
import { Container, Chapter, FadeUp } from "./primitives";

const PROBLEMS = [
  {
    icon: Search,
    title: "Manual searching wastes hours",
    body: "Scrolling feeds, saving links, jumping between tabs. Your best hours vanish into research instead of revenue.",
    size: "lg:col-span-2 lg:row-span-2",
    accent: "from-[#7C3AED]/25 to-transparent",
  },
  {
    icon: MessageSquareOff,
    title: "Conversations disappear",
    body: "Threads bury themselves. Warm intent goes cold before you notice.",
  },
  {
    icon: Database,
    title: "No centralized CRM",
    body: "Spreadsheets, sticky notes, and DMs. No source of truth.",
  },
  {
    icon: BrainCircuit,
    title: "No AI insights",
    body: "You’re guessing at intent, priority, and next action.",
    accent: "from-[#4F46E5]/25 to-transparent",
  },
  {
    icon: ShieldCheck,
    title: "No approval workflow",
    body: "Team messages go out unchecked — brand risk you didn’t sign up for.",
  },
  {
    icon: Gauge,
    title: "Hard to scale engagement",
    body: "What works for 50 leads breaks at 500. Growth stalls at the operator.",
    size: "lg:col-span-2",
  },
];

export default function Problem() {
  return (
    <section id="problem" data-testid="problem-section" className="relative py-32 md:py-40 border-t border-white/10">
      {/* Ambient */}
      <div className="orb bg-[#7C3AED] w-[520px] h-[520px] top-[10%] -left-32 opacity-25" />

      <Container className="relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <Chapter
            number="01"
            kicker="The Problem"
            title={
              <>
                Finding qualified leads on social{" "}
                <span className="font-serif-x italic text-white/80">shouldn’t</span> be manual.
              </>
            }
            description="Modern buyers announce their intent in public — but capturing, qualifying and engaging them by hand is a full-time job. That’s the tax you’re paying today."
          />
          <FadeUp delay={0.2}>
            <div className="flex flex-col items-start lg:items-end gap-2">
              <div className="font-mono-x text-[10px] tracking-[0.22em] uppercase text-white/40">
                Time lost per rep / week
              </div>
              <div className="font-display text-6xl md:text-7xl tracking-[-0.04em] text-white">
                14h<span className="text-white/30">.6</span>
              </div>
              <div className="text-xs text-white/50 font-mono-x">— internal benchmark, 2025</div>
            </div>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROBLEMS.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.06}>
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className={`group relative rounded-3xl border border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04] transition-colors p-6 md:p-8 h-full min-h-[220px] flex flex-col ${p.size || ""}`}
              >
                {p.accent && (
                  <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${p.accent} opacity-70 pointer-events-none`} />
                )}
                <div className="relative flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                    <p.icon className="h-5 w-5 text-white/80" strokeWidth={1.5} />
                  </span>
                  <span className="font-mono-x text-[10px] tracking-[0.2em] text-white/30">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h4 className="relative mt-8 font-display text-xl md:text-2xl tracking-[-0.02em] leading-tight text-white">
                  {p.title}
                </h4>
                <p className="relative mt-3 text-sm md:text-[15px] text-white/55 leading-relaxed">
                  {p.body}
                </p>
              </motion.div>
            </FadeUp>
          ))}
        </div>
      </Container>
    </section>
  );
}
